import type { CSSProperties } from "react";
import type { TripStatus } from "@tc/shared";

// Lifecycle → colour token, matching the prototype's STATUS_META and docs/design-system.md §4.
export type BadgeStatus =
  | "enquiry"
  | "proposal"
  | "accepted"
  // Green "success" tone. No trip status maps here anymore (the trip "Confirmed" state was removed),
  // but invoice `Paid` reuses this palette — keep it as a shared positive-state tone.
  | "confirmed"
  | "completed"
  | "cancelled"
  | "postponed"
  | "expired";

export const STATUS_META: Record<BadgeStatus, { c: string; bg: string }> = {
  enquiry: { c: "var(--slate)", bg: "var(--slate-soft)" },
  proposal: { c: "var(--primary)", bg: "var(--primary-soft)" },
  accepted: { c: "var(--info)", bg: "var(--info-soft)" },
  confirmed: { c: "var(--success)", bg: "var(--success-soft)" },
  completed: { c: "var(--slate)", bg: "var(--slate-soft)" },
  cancelled: { c: "var(--danger)", bg: "var(--danger-soft)" },
  postponed: { c: "var(--warning)", bg: "var(--warning-soft)" },
  expired: { c: "var(--danger)", bg: "var(--danger-soft)" },
};

/** Map a domain TripStatus (data-model §9) onto its badge palette key. */
export const TRIP_STATUS_BADGE: Record<TripStatus, BadgeStatus> = {
  Enquiry: "enquiry",
  ProposalSent: "proposal",
  Accepted: "accepted",
  Completed: "completed",
  Cancelled: "cancelled",
  Postponed: "postponed",
  Expired: "expired",
};

export interface StatusBadgeProps {
  status: BadgeStatus;
  /** Localised label to display (callers pass the translated string). */
  label: string;
}

export function StatusBadge({ status, label }: StatusBadgeProps) {
  const meta = STATUS_META[status] ?? STATUS_META.enquiry;
  const style: CSSProperties = { color: meta.c, background: meta.bg };
  return (
    <span className="badge" style={style}>
      <span className="badge-dot" style={{ background: meta.c }} />
      {label}
    </span>
  );
}
